import { Navigate, RouteObject } from 'react-router';
import PermissionGuard from '../gate/PermissionGuard';
import { PERMISSIONS } from '@/shared/constants/permissions';
import { usePermissions } from '@/features/access-control';
import { paths } from './path';
import { Skeleton } from '@/shared/ui/skeleton';

const SETTINGS_ROUTES_ORDER = [
	{ path: 'user-management', permission: PERMISSIONS.EMPLOYEE_READ },
	{ path: 'delegations', permission: PERMISSIONS.DELEGATION_READ },
	{
		path: 'operations',
		permission: [PERMISSIONS.ERP_READ, PERMISSIONS.FISCAL_DATA_READ, PERMISSIONS.SETTING_REFUNDS],
	},
	{ path: 'billing', permission: [PERMISSIONS.FISCAL_DATA_READ, PERMISSIONS.ZOHO_BILLING] },
] as const;

const SettingsIndexRedirect = () => {
	const { data: permissions, isLoading } = usePermissions();

	if (isLoading)
		return (
			<div className='flex flex-col gap-3 p-6'>
				<Skeleton
					height={40}
					borderRadius={8}
				/>
				<Skeleton
					height={40}
					borderRadius={8}
				/>
				<Skeleton
					height={40}
					borderRadius={8}
				/>
			</div>
		);

	const first = SETTINGS_ROUTES_ORDER.find(({ permission }) => {
		const perms = Array.isArray(permission) ? permission : [permission];
		return perms.some((p) => permissions?.has(p));
	});

	if (!first)
		return (
			<Navigate
				to={paths.home()}
				replace
			/>
		);

	return (
		<Navigate
			to={first.path}
			replace
		/>
	);
};

export const settingsRoutes: RouteObject[] = [
	{
		path: 'settings',
		children: [
			{
				lazy: async () => {
					const { default: Component } = await import('../pages/SettingsPage');
					return { Component };
				},
				children: [
					{
						index: true,
						element: <SettingsIndexRedirect />,
					},
					{
						path: 'user-management',
						children: [
							{
								index: true,
								lazy: async () => {
									const { default: Page } = await import(
										'@/features/access-control/pages/UserManagementPage'
									);
									return {
										Component: () => (
											<PermissionGuard permission={PERMISSIONS.EMPLOYEE_READ}>
												<Page />
											</PermissionGuard>
										),
									};
								},
							},
							{
								path: 'create',
								lazy: async () => {
									const { default: Page } = await import(
										'@/features/access-control/roles/pages/AddProfilePage'
									);
									return {
										Component: () => (
											<PermissionGuard
												permission={[PERMISSIONS.EMPLOYEE_CREATE, PERMISSIONS.ROLE_CREATE]}
												mode='any'
											>
												<Page />
											</PermissionGuard>
										),
									};
								},
							},
							{
								path: 'roles/:roleId/edit',
								lazy: async () => {
									const { default: Page } = await import(
										'@/features/access-control/roles/pages/EditRolePage'
									);
									return {
										Component: () => (
											<PermissionGuard permission={PERMISSIONS.ROLE_EDIT}>
												<Page />
											</PermissionGuard>
										),
									};
								},
							},
						],
					},
					{
						path: 'delegations',
						lazy: async () => {
							const { default: Page } = await import(
								'@/features/access-control/delegations/pages/DelegationsLayout'
							);
							return {
								Component: () => (
									<PermissionGuard permission={PERMISSIONS.DELEGATION_READ}>
										<Page />
									</PermissionGuard>
								),
							};
						},
						children: [
							{
								index: true,
								lazy: async () => {
									const { default: Component } = await import(
										'@/features/access-control/delegations/pages/DelegationsListPage'
									);
									return { Component };
								},
							},
							{
								path: ':delegationId',
								lazy: async () => {
									const { default: Component } = await import(
										'@/features/access-control/delegations/pages/DelegationDetailsPage'
									);
									return { Component };
								},
							},
							{
								path: 'create',
								lazy: async () => {
									const { default: Component } = await import(
										'@/features/access-control/delegations/pages/CreateDelegationPage'
									);
									return { Component };
								},
							},
						],
					},

					{
						path: 'operations',
						lazy: async () => {
							const { default: Page } = await import('@/features/company/pages/OperationsPage');
							return {
								Component: () => (
									<PermissionGuard
										permission={[
											PERMISSIONS.ERP_READ,
											PERMISSIONS.FISCAL_DATA_READ,
											PERMISSIONS.SETTING_REFUNDS,
										]}
										mode='any'
									>
										<Page />
									</PermissionGuard>
								),
							};
						},
					},
					{
						path: 'billing',
						lazy: async () => {
							const { default: Page } = await import('@/features/company/pages/BillingPage');
							return {
								Component: () => (
									<PermissionGuard
										permission={[PERMISSIONS.FISCAL_DATA_READ, PERMISSIONS.ZOHO_BILLING]}
										mode='any'
									>
										<Page />
									</PermissionGuard>
								),
							};
						},
					},
				],
			},
		],
	},
];
